import { useState, useEffect } from 'react';
import { Card, Select, Transfer, Button, Typography, Alert } from 'antd';
import { getAllSubjects } from '../services/subjectService';
import { getAllClasses } from '../services/classService';
import { getClassSubjects, saveClassSubjects } from '../services/assignmentService';
import { useNotification } from '../context/NotificationContext';

const { Title } = Typography;
const { Option } = Select;

const ClassSubjectsPage = () => {
    const { message } = useNotification();

    const [classes, setClasses] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [loading, setLoading] = useState(true);

    const [selectedClass, setSelectedClass] = useState(null);
    const [targetKeys, setTargetKeys] = useState([]);
    const [saving, setSaving] = useState(false);
    const [loadingSubjects, setLoadingSubjects] = useState(false);

    useEffect(() => {
        const loadMaster = async () => {
            setLoading(true);
            try {
                const [cls, subs] = await Promise.all([getAllClasses(), getAllSubjects()]);
                setClasses(cls);
                setSubjects(subs.map(s => ({ ...s, key: s.id.toString() })));
            } catch (error) {
                console.error('Failed to load master data', error);
                message.error('Failed to load classes or subjects');
            } finally {
                setLoading(false);
            }
        };
        loadMaster();
    }, []);

    const handleClassSelect = async (classId) => {
        setSelectedClass(classId);
        if (!classId) {
            setTargetKeys([]);
            return;
        }
        setLoadingSubjects(true);
        try {
            const assigned = await getClassSubjects(classId);
            setTargetKeys(assigned.map(id => id.toString()));
        } catch (error) {
            console.error('Failed to fetch class subjects', error);
            message.error('Failed to load class subjects');
            setTargetKeys([]);
        } finally {
            setLoadingSubjects(false);
        }
    };

    const handleSave = async () => {
        if (!selectedClass) return;
        setSaving(true);
        try {
            await saveClassSubjects(selectedClass, targetKeys.map(k => parseInt(k)));
            message.success('Subjects saved for the selected class');
        } catch (error) {
            console.error('Failed to save class subjects', error);
            message.error(error?.response?.data?.error || 'Failed to save subjects for class');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div>
            <Title level={2} style={{ marginBottom: 16 }}>Subjects per Class</Title>
            <Card loading={loading} style={{ minHeight: 400 }}>
                <Alert message="Class Subject Setup" description="Select a class and move subjects to the right to include them in the class's subject list for the current academic year." type="info" showIcon style={{ marginBottom: 16 }} />
                <Select showSearch placeholder="Select a class" style={{ width: '100%', marginBottom: 16 }} onChange={handleClassSelect} disabled={loading}>
                    {classes.map(c => <Option key={c.id} value={c.id}>{c.name}</Option>)}
                </Select>

                {selectedClass && (
                    <>
                        <Transfer
                            dataSource={subjects}
                            targetKeys={targetKeys}
                            onChange={setTargetKeys}
                            render={item => item.name}
                            listStyle={{ width: '100%', height: 360 }}
                            disabled={loadingSubjects}
                            titles={['Available Subjects', 'Assigned Subjects']}
                        />
                        <Button type="primary" style={{ width: '100%', marginTop: 16 }} onClick={handleSave} loading={saving}>Save Subjects for {classes.find(c => c.id === selectedClass)?.name}</Button>
                    </>
                )}
            </Card>
        </div>
    );
};

export default ClassSubjectsPage;